import React from "react";
import { ArrowUpRight, Wallet, Hourglass, Undo2, Receipt, Scale } from "lucide-react";

export const money = (n) => `NZ$${Number(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const Tile = ({ label, value, sub, Icon, color, testid }) => (
  <div className="bg-white border p-4" style={{ borderColor: "#E8DFCB" }} data-testid={testid}>
    <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#7A6A55]"><Icon className="w-4 h-4" style={{ color }} /> {label}</div>
    <div className="font-serif text-2xl sm:text-3xl mt-2" style={{ color }}>{value}</div>
    {sub && <div className="text-[11px] text-[#9C8B73] mt-1">{sub}</div>}
  </div>
);

export function FinanceDashboard({ data, onOpenOrder }) {
  if (!data) return null;
  const t = data.totals, c = data.counts;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Tile label="Sales" value={money(t.sales)} sub={`${c.orders} order${c.orders === 1 ? "" : "s"}`} Icon={ArrowUpRight} color="#3E1F46" testid="finance-tile-sales" />
        <Tile label="Cash in" value={money(t.cash_in)} sub={`${c.paid} paid · ${c.partial} part paid`} Icon={Wallet} color="#2F6B4F" testid="finance-tile-cash" />
        <Tile label="Owing" value={money(t.owing)} sub={`${c.unpaid + c.partial} order${c.unpaid + c.partial === 1 ? "" : "s"} to chase`} Icon={Hourglass} color="#C49247" testid="finance-tile-owing" />
        <Tile label="Refunds & credits" value={money(t.refunds)} Icon={Undo2} color="#A33030" testid="finance-tile-refunds" />
        <Tile label="Expenses" value={money(t.expenses)} sub={t.manual_income ? `+ ${money(t.manual_income)} other income` : "manual entries"} Icon={Receipt} color="#A14C4C" testid="finance-tile-expenses" />
        <Tile label="Net cash" value={money(t.net)} sub="cash in + other income , refunds , expenses" Icon={Scale} color={t.net >= 0 ? "#2F6B4F" : "#A33030"} testid="finance-tile-net" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="bg-white border p-4" style={{ borderColor: "#E8DFCB" }} data-testid="finance-by-channel">
          <h2 className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] mb-3">Sales by channel</h2>
          {data.by_kind.length === 0 && <p className="text-sm italic text-[#9C8B73]">No sales in this range.</p>}
          {data.by_kind.map((b) => {
            const pct = t.sales ? Math.round((b.sales / t.sales) * 100) : 0;
            return (
              <div key={b.kind} className="mb-2.5">
                <div className="flex justify-between text-sm text-[#3C2A1E]"><span>{b.label} <span className="text-[#9C8B73] text-xs">· {b.orders}</span></span><strong>{money(b.sales)}</strong></div>
                <div className="h-1.5 bg-[#F3EBDD] mt-1"><div className="h-1.5 bg-[#6E1E3A]" style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </section>

        <section className="bg-white border p-4" style={{ borderColor: "#E8DFCB" }} data-testid="finance-top-owing">
          <h2 className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] mb-3">Owing , largest first</h2>
          {data.top_owing.length === 0 && <p className="text-sm italic text-[#9C8B73]">Nothing owing. Lovely.</p>}
          <ul className="divide-y" style={{ borderColor: "#F3EBDD" }}>
            {data.top_owing.map((o) => (
              <li key={o.order_id}>
                <button type="button" onClick={() => onOpenOrder(o.order_id)} className="w-full flex items-center justify-between py-2 text-left hover:bg-[#FBF6E9] px-1" data-testid={`finance-owing-${o.order_id}`}>
                  <span className="text-sm text-[#3C2A1E] truncate"><span className="text-[#6E1E3A] font-semibold">{o.order_number || "—"}</span> · {o.customer_name || o.email}</span>
                  <span className="font-semibold text-[#C49247] whitespace-nowrap ml-3">{money(o.owing)}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <MonthlyBars months={data.monthly} />
    </div>
  );
}

export function MonthlyBars({ months }) {
  if (!months?.length) return null;
  const max = Math.max(1, ...months.map((m) => Math.max(m.sales, m.cash_in, m.refunds + m.expenses)));
  return (
    <section className="bg-white border p-4" style={{ borderColor: "#E8DFCB" }} data-testid="finance-monthly">
      <h2 className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] mb-3">Month by month</h2>
      <div className="flex items-end gap-3 overflow-x-auto pb-2" style={{ minHeight: 160 }}>
        {months.map((m) => (
          <div key={m.month} className="flex flex-col items-center gap-1 min-w-[64px]" title={`Sales ${money(m.sales)} · Cash ${money(m.cash_in)} · Out ${money(m.refunds + m.expenses)}`}>
            <div className="flex items-end gap-0.5 h-[110px]">
              <div className="w-4 bg-[#3E1F46]" style={{ height: `${(m.sales / max) * 100}%` }} />
              <div className="w-4 bg-[#2F6B4F]" style={{ height: `${(m.cash_in / max) * 100}%` }} />
              <div className="w-4 bg-[#A33030]" style={{ height: `${((m.refunds + m.expenses) / max) * 100}%` }} />
            </div>
            <div className="text-[10px] text-[#7A6A55]">{m.month}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-4 text-[10px] uppercase tracking-[0.18em] text-[#7A6A55] mt-1">
        <span><i className="inline-block w-3 h-3 bg-[#3E1F46] mr-1 align-middle" />Sales</span>
        <span><i className="inline-block w-3 h-3 bg-[#2F6B4F] mr-1 align-middle" />Cash in</span>
        <span><i className="inline-block w-3 h-3 bg-[#A33030] mr-1 align-middle" />Refunds + expenses</span>
      </div>
    </section>
  );
}
